import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Badge, Form, FormGroup, Label, Input, Button } from 'reactstrap';
import { useTenantContext } from '../../auth/useTenantContext';
import { EditAccordion, type EditAccordionTab } from '../ui/EditAccordion';
import { VpSelect } from '../ui/VpSelect';
import { useRole } from '../../auth/useRole';
import { AuditLogTable } from '../audit/AuditLogTable';
import { WarehouseExcludedLocations } from './WarehouseExcludedLocations';
import { WarehouseDailyMailSettings } from './WarehouseDailyMailSettings';
import {
  useTenantAdminWarehouses,
  useCreateAdminWarehouse,
  useUpdateAdminWarehouse,
  useSuspendWarehouse,
  useReactivateWarehouse,
  type Warehouse,
} from '../../api/adminWarehouses';
import { IANA_TIMEZONES, DEFAULT_TIMEZONE } from '../../constants/ianaTimezones';
import { type AutoSplitConfigValue } from '../picklists/PickListAutoSplitConfigSection';

type CreateProps = { mode: 'create'; isOpen: boolean; onClose: () => void };
type EditProps = {
  mode: 'edit';
  initial: {
    id: number;
    code: string;
    name: string;
    timezone: string;
    address: string | null;
    auto_split_mode?: 'soft' | 'hard';
    auto_split_item_count_threshold?: number | null;
    auto_split_per_row_qty_threshold?: number | null;
    auto_split_target_qty_per_operator?: number;
    max_qty_per_mission?: number;
    sftp_inbound_folder?: string | null;
    daily_partials_mail_enabled?: boolean;
    daily_partials_mail_time?: string;
    daily_partials_mail_recipients?: string[];
    daily_partials_mail_last_closed_on?: string | null;
    // SP-A T7b — stato corrente; assente ⇒ trattato come 'active'.
    status?: 'active' | 'suspended' | 'deleted';
  };
  isOpen: boolean;
  onClose: () => void;
};
type Props = CreateProps | EditProps;

/**
 * Warehouse create/edit surface. SP-5m: rendered inline as the shared
 * master-detail `EditAccordion` (tabs: Dati anagrafici · Auto-split · Audit),
 * NOT a side drawer. Submit/validation/error/idempotency logic unchanged.
 * Name kept for back-compat with existing importers/tests.
 */
export function AdminWarehouseDrawer(props: Props) {
  // Solo l'amministratore di azienda (o di piattaforma) scrive le ubicazioni
  // escluse: sono gli stessi che l'endpoint autorizza.
  const { isAnyAdmin } = useRole();
  const isEdit = props.mode === 'edit';
  const initial = isEdit ? props.initial : { code: '', name: '', timezone: DEFAULT_TIMEZONE, address: null };
  const [code, setCode] = useState(initial.code);
  const [name, setName] = useState(initial.name);
  const [timezone, setTimezone] = useState(initial.timezone);
  const [address, setAddress] = useState(initial.address ?? '');
  // picking-indexes — colli massimi consigliati per missione (default 100).
  const [maxQtyPerMission, setMaxQtyPerMission] = useState<number>(
    isEdit ? (props.initial.max_qty_per_mission ?? 100) : 100,
  );
  const [sftpFolder, setSftpFolder] = useState<string>(isEdit ? (props.initial.sftp_inbound_folder ?? '') : '');
  const [sftpError, setSftpError] = useState<string | null>(null);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [genericError, setGenericError] = useState<string | null>(null);
  const [idemKey] = useState(() => crypto.randomUUID());

  const initialAutoSplit: AutoSplitConfigValue = isEdit && (props as EditProps).initial.auto_split_mode
    ? {
        item_count_threshold:    (props as EditProps).initial.auto_split_item_count_threshold ?? null,
        per_row_qty_threshold:   (props as EditProps).initial.auto_split_per_row_qty_threshold ?? null,
        mode:                    ((props as EditProps).initial.auto_split_mode ?? 'soft') as 'soft' | 'hard',
        target_qty_per_operator: (props as EditProps).initial.auto_split_target_qty_per_operator ?? 50,
      }
    : {
        item_count_threshold:    null,
        per_row_qty_threshold:   null,
        mode:                    'soft',
        target_qty_per_operator: 50,
      };
  // setter non più usato: la sezione Auto-split è nascosta; resta lo stato con
  // i default per inviare comunque la config al backend.
  const [autoSplit] = useState<AutoSplitConfigValue>(initialAutoSplit);

  const { t } = useTranslation('warehouse');
  const { token, tenantCode } = useTenantContext();
  const createMut = useCreateAdminWarehouse(token, tenantCode);
  const updateMut = useUpdateAdminWarehouse(token, tenantCode);
  const suspendMut = useSuspendWarehouse(token, tenantCode);
  const reactivateMut = useReactivateWarehouse(token, tenantCode);
  const [statusError, setStatusError] = useState<string | null>(null);
  // SP-A — in creazione si eredita la configurazione da un magazzino esistente:
  // senza, il magazzino nascerebbe inutilizzabile (l'import fallisce per schema
  // mancante) e servirebbe un amministratore di piattaforma per completarlo.
  const { data: existingWarehouses } = useTenantAdminWarehouses(token, tenantCode);
  const copySources = (existingWarehouses?.data ?? []).filter((w: Warehouse) => w.status !== 'suspended');
  const [copyFrom, setCopyFrom] = useState<number | null>(null);

  const autoSplitInvalid = autoSplit.mode === 'hard'
    && autoSplit.item_count_threshold === null
    && autoSplit.per_row_qty_threshold === null;

  const autoSplitFields = {
    auto_split_item_count_threshold:    autoSplit.item_count_threshold,
    auto_split_per_row_qty_threshold:   autoSplit.per_row_qty_threshold,
    auto_split_mode:                    autoSplit.mode,
    auto_split_target_qty_per_operator: autoSplit.target_qty_per_operator,
  };

  const save = async () => {
    setCodeError(null);
    setGenericError(null);
    setSftpError(null);
    const folder = sftpFolder.trim();
    if (folder !== '' && !/^[a-z0-9_-]{1,64}$/.test(folder)) {
      setSftpError(t('sftpFolder.invalid'));
      return;
    }
    const sftp_inbound_folder = folder === '' ? null : folder;
    try {
      if (isEdit) {
        await updateMut.mutateAsync({
          id: (props as EditProps).initial.id,
          patch: { name, timezone, address: address || null, max_qty_per_mission: maxQtyPerMission, sftp_inbound_folder, ...autoSplitFields },
        });
      } else {
        await createMut.mutateAsync({
          body: { code, name, timezone, address: address || null, max_qty_per_mission: maxQtyPerMission, sftp_inbound_folder,
                  copy_config_from_warehouse_id: copyFrom ?? copySources[0]?.id ?? null,
                  ...autoSplitFields },
          idempotencyKey: idemKey,
        });
      }
      props.onClose();
    } catch (err: unknown) {
      const e = err as { body?: { code?: string; detail?: string }; message?: string };
      const c = e?.body?.code;
      if (c === 'warehouse_code_exists') {
        setCodeError('Codice già esistente — scegline uno diverso');
      } else if (c === 'sftp_folder_invalid') {
        setSftpError(t('sftpFolder.invalid'));
      } else if (c === 'sftp_folder_exists') {
        setSftpError(t('sftpFolder.exists'));
      } else {
        setGenericError(e?.body?.detail ?? e?.message ?? 'Errore sconosciuto');
      }
    }
  };

  const anagrafica = (
    <Form onSubmit={(e) => { e.preventDefault(); void save(); }} className="d-flex flex-column gap-2">
      <FormGroup>
        <Label for="wh-code">Code</Label>
        <Input id="wh-code" aria-label="Code" value={code}
               onChange={(e) => setCode(e.target.value.toUpperCase())}
               disabled={isEdit} pattern="^[A-Z][A-Z0-9-]+$" maxLength={32}
               invalid={codeError !== null} required />
        {codeError && <span role="alert" className="text-danger small">{codeError}</span>}
        {/* SP-A — il campo era già disabilitato in edit, ma senza spiegazione:
            chi lo trova bloccato non sa perché né come procedere. */}
        {isEdit && <span className="form-text text-muted">{t('code.immutableHint')}</span>}
      </FormGroup>
      <FormGroup>
        <Label for="wh-name">Nome</Label>
        <Input id="wh-name" aria-label="Nome" value={name}
               onChange={(e) => setName(e.target.value)} maxLength={128} required />
      </FormGroup>
      <FormGroup>
        <Label>Timezone</Label>
        <VpSelect
          ariaLabel="Timezone"
          options={IANA_TIMEZONES.map((tz) => ({ value: tz, label: tz }))}
          value={timezone}
          onChange={(v) => setTimezone(String(v ?? ''))}
        />
      </FormGroup>
      <FormGroup>
        <Label for="wh-address">Indirizzo</Label>
        <Input id="wh-address" aria-label="Indirizzo"
               value={address} onChange={(e) => setAddress(e.target.value)} maxLength={256} />
      </FormGroup>
      <FormGroup>
        <Label for="wh-sftp-folder">{t('sftpFolder.label')}</Label>
        <Input id="wh-sftp-folder" aria-label={t('sftpFolder.label')} value={sftpFolder}
               onChange={(e) => setSftpFolder(e.target.value.toLowerCase())}
               maxLength={64} invalid={sftpError !== null} />
        {sftpError && <span role="alert" className="text-danger small">{sftpError}</span>}
        <span className="form-text text-muted d-block">{t('sftpFolder.hint')}</span>
      </FormGroup>
      {!isEdit && copySources.length > 0 && (
        <FormGroup>
          <Label>{t('create.copyConfigFrom')}</Label>
          <VpSelect
            ariaLabel={t('create.copyConfigFrom')}
            options={copySources.map((w: Warehouse) => ({ value: w.id, label: `${w.code} — ${w.name}` }))}
            value={copyFrom ?? copySources[0].id}
            onChange={(v) => setCopyFrom(v === null ? null : Number(v))}
          />
          <span className="form-text text-muted">{t('create.copyConfigHint')}</span>
        </FormGroup>
      )}
      <FormGroup>
        <Label for="wh-max-qty">Max colli per missione</Label>
        <Input id="wh-max-qty" aria-label="Max colli per missione" type="number"
               min={1} step={1} value={maxQtyPerMission}
               onChange={(e) => setMaxQtyPerMission(Math.max(1, Number(e.target.value) || 0))} />
        <span className="form-text text-muted">
          Soglia consigliata di colli per una missione. Oltre questa soglia l'indice di calore (ICM) aumenta e la distinta viene segnalata come "da frammentare".
        </span>
      </FormGroup>
    </Form>
  );

  // SP-A T7b — tab Stato: sospendi / riattiva.
  //
  // È il passo che rende praticabile la sostituzione di un codice: il codice è
  // immutabile, quindi si crea un magazzino nuovo e si sospende il vecchio —
  // altrimenti il vecchio resterebbe attivo e continuerebbe ad accettare liste.
  const currentStatus = isEdit ? ((props as EditProps).initial.status ?? 'active') : 'active';
  const isSuspended = currentStatus === 'suspended';

  const changeStatus = async (action: 'suspend' | 'reactivate') => {
    setStatusError(null);
    try {
      const id = (props as EditProps).initial.id;
      if (action === 'suspend') await suspendMut.mutateAsync({ id });
      else await reactivateMut.mutateAsync({ id });
    } catch (err: unknown) {
      const e = err as {
        body?: { code?: string; detail?: string; active_missions?: number; active_sessions?: number };
        message?: string;
      };
      if (e?.body?.code === 'warehouse_has_active_work') {
        // Si compone il messaggio dai due conteggi separati, omettendo la parte
        // a zero: "3 missioni in corso" è azionabile, "3 missioni e 0 operatori" no.
        const parts: string[] = [];
        const m = e.body.active_missions ?? 0;
        const ss = e.body.active_sessions ?? 0;
        if (m > 0) parts.push(t('status.activeWork.missions', { count: m }));
        if (ss > 0) parts.push(t('status.activeWork.sessions', { count: ss }));
        setStatusError(t('status.activeWork.blocked', { what: parts.join(' e ') }));
      } else {
        setStatusError(e?.body?.detail ?? e?.message ?? 'Errore sconosciuto');
      }
    }
  };

  const statoTab = (
    <div className="d-flex flex-column gap-3">
      <div>
        <Label className="mb-1">{t('status.current')}</Label>
        <div>
          <Badge color={isSuspended ? 'warning' : 'success'} pill>
            {isSuspended ? t('status.suspended') : t('status.active')}
          </Badge>
        </div>
        <span className="form-text text-muted">
          {isSuspended ? t('status.suspendedHint') : t('status.activeHint')}
        </span>
      </div>

      {statusError && <Alert color="danger" className="mb-0" role="alert">{statusError}</Alert>}

      <div>
        {isSuspended ? (
          <Button type="button" color="success" outline
                  disabled={reactivateMut.isPending}
                  onClick={() => void changeStatus('reactivate')}>
            <i className="bi bi-play-circle me-1" />{t('status.reactivate')}
          </Button>
        ) : (
          <Button type="button" color="warning" outline
                  disabled={suspendMut.isPending}
                  onClick={() => {
                    if (window.confirm(t('status.suspendConfirm', { code: initial.code }))) {
                      void changeStatus('suspend');
                    }
                  }}>
            <i className="bi bi-pause-circle me-1" />{t('status.suspend')}
          </Button>
        )}
      </div>
    </div>
  );

  // bug_fixes_tpicking_development — la sezione Auto-split (Soft/Hard) è nascosta
  // per ora (non richiesta). Il magazzino viene comunque salvato con la config di
  // default (mode 'soft', nessuna soglia → nessun auto-split) via autoSplitFields.
  const tabs: EditAccordionTab[] = [
    { id: 'anagrafica', label: 'Dati anagrafici', content: anagrafica },
  ];
  if (isEdit) {
    tabs.push({ id: 'stato', label: t('status.tab'), content: statoTab });
    tabs.push({
      id: 'ubicazioni-escluse',
      label: 'Ubicazioni escluse',
      content: (
        <WarehouseExcludedLocations
          warehouseId={(props as EditProps).initial.id}
          canEdit={isAnyAdmin}
        />
      ),
    });
    tabs.push({
      id: 'notifiche',
      label: t('dailyMail.tab'),
      content: (
        <WarehouseDailyMailSettings
          warehouseId={(props as EditProps).initial.id}
          initial={{
            enabled: (props as EditProps).initial.daily_partials_mail_enabled ?? false,
            time: (props as EditProps).initial.daily_partials_mail_time ?? '18:00',
            recipients: (props as EditProps).initial.daily_partials_mail_recipients ?? [],
            lastClosedOn: (props as EditProps).initial.daily_partials_mail_last_closed_on ?? null,
          }}
          canEdit={isAnyAdmin}
        />
      ),
    });
    tabs.push({
      id: 'audit',
      label: 'Audit log',
      content: (
        <AuditLogTable
          apiBase="tenant" availableTenants={[]} availableActors={[]} hideAdvanced
          initialScope={{ entity_types: ['warehouse'], entity_id: String((props as EditProps).initial.id) }}
        />
      ),
    });
  }

  const footer = (
    <>
      {genericError && <span role="alert" className="text-danger small me-auto align-self-center">{genericError}</span>}
      <Button type="button" color="light" onClick={props.onClose}>Annulla</Button>
      <Button type="button" color="primary" disabled={autoSplitInvalid} onClick={() => void save()}>Salva</Button>
    </>
  );

  return (
    <EditAccordion
      isOpen={props.isOpen}
      onClose={props.onClose}
      icon="bi-building"
      code={isEdit ? initial.code : undefined}
      title={isEdit ? initial.name : 'Nuovo magazzino'}
      tabs={tabs}
      footer={footer}
    />
  );
}
